// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/holiday/components/HolidayScare.module.css';
import {
	type HolidayScareParticle,
	type HolidayScareVariant,
	SCARE_DURATION_MS,
} from '@app/features/holiday/state/Holiday';
import {dimScareOccluders} from '@app/features/holiday/utils/holidayScareOccluders';
import {clsx} from 'clsx';
import type React from 'react';
import {
	type ComponentType,
	type LazyExoticComponent,
	lazy,
	Suspense,
	type SVGProps,
	useEffect,
	useRef,
	useState,
} from 'react';

const importSkeletonPeeking = () =>
	import('@app/features/holiday/packs/halloween/assets/scares/skeleton-peeking.svg?react');

/**
 * The skeleton is ~283 KB of inline SVG. Imported statically it sat in the main chunk all year,
 * for every user, whether or not a scare ever fires. Splitting it keeps that weight out of the
 * baseline heap, which matters most on memory-constrained mobile clients.
 */
const SkeletonPeeking = lazy(importSkeletonPeeking);

type ScareArt = ComponentType<SVGProps<SVGSVGElement>> | LazyExoticComponent<ComponentType<SVGProps<SVGSVGElement>>>;

const SCARE_ART: Record<HolidayScareVariant, ScareArt> = {
	skeleton: SkeletonPeeking,
};

/**
 * Warms the art chunk while the season is on, so the first scare of a session is not the one
 * that pays for the fetch. Safe to call repeatedly: the module registry dedupes.
 */
export function prefetchHolidayScareArt(): void {
	void importSkeletonPeeking();
}

/**
 * Takes its own elapsed-time snapshot instead of reusing the wrapper's.
 *
 * The art comes from a lazy chunk, so on a cold first scare it can mount after the occluder fade
 * has already begun. That fade is driven from the wrapper's snapshot, so measuring again here is
 * what keeps the sprite at the same point in the animation as the fade it is supposed to match.
 */
function HolidayScareArt({particle}: {particle: HolidayScareParticle}): React.ReactElement {
	const Art = SCARE_ART[particle.variant];
	const [elapsedMs] = useState(() => Math.max(0, Math.min(SCARE_DURATION_MS, Date.now() - particle.spawnedAt)));
	return (
		<Art
			className={clsx(styles.scare, particle.mode === 'pop' ? styles.pop : styles.grow)}
			style={
				{
					'--scare-duration': `${SCARE_DURATION_MS}ms`,
					'--scare-delay': `${-elapsedMs}ms`,
				} as React.CSSProperties
			}
			data-flx="holiday.holiday-scare.scare"
		/>
	);
}

export function HolidayScare({particle}: {particle: HolidayScareParticle}): React.ReactElement {
	const boxRef = useRef<HTMLDivElement | null>(null);

	// A scare survives a channel switch, and switching views re-parents this node, which restarts the
	// animation. Offsetting the delay by however long it has already been alive resumes it mid-flight
	// instead of replaying the grow-in. The store owns the actual lifetime, measured from spawn.
	//
	// Snapshotted once, deliberately. This component is re-created on every pass of the observer that
	// renders the particle list, so any unrelated particle churn — a bat flock spawning, a ghost
	// despawning — would otherwise recompute a larger value mid-flight. Writing that into
	// `animation-delay` does not restart the animation, it jumps its active time, so the sprite would
	// skip ahead while the occluder fade (snapshotted in the effect below) stayed put.
	const [elapsedMs] = useState(() => Math.max(0, Math.min(SCARE_DURATION_MS, Date.now() - particle.spawnedAt)));
	const mode = particle.mode;

	useEffect(() => {
		const box = boxRef.current;
		if (box == null) return;
		// The wrapper is not animated, so its rect is the sprite's settled position from the first
		// frame. Measuring the sprite instead would read its scaled-down starting pose.
		return dimScareOccluders(box, box.getBoundingClientRect(), {
			durationMs: SCARE_DURATION_MS,
			delayMs: -elapsedMs,
			mode,
		});
		// Deliberately not keyed on elapsed: the delay is a mount-time snapshot, and re-running would
		// re-measure the occluders mid-animation.
	}, [mode]);

	// The wrapper carries its own geometry, so it measures correctly before the art chunk lands.
	return (
		<div ref={boxRef} className={styles.box} aria-hidden="true" data-flx="holiday.holiday-scare.box">
			<Suspense fallback={null}>
				<HolidayScareArt particle={particle} data-flx="holiday.holiday-scare.holiday-scare-art" />
			</Suspense>
		</div>
	);
}
